import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

// The science of Riversol — a single, coherent Hinokitiol / Beta-T story.
// Imagery, headline, copy and facts all belong to the SAME ingredient.
// Verified from riversol.com's Beta-Thujaplicin article:
//   • "organic compound found in the oil of the Pacific red cedar"
//   • antioxidant, anti-inflammatory, anti-pigmentation, antibiotic
//   • "more than 120 peer-reviewed scientific articles"
//   • Dr. Rivers encountered it through his work at UBC
//   • "years of formulation chemistry" to keep it stable
const FACTS = [
  { label: "Origin", body: "A natural compound from the oil of the Pacific red cedar." },
  { label: "Research", body: "120+ peer-reviewed studies on its biological effects." },
  { label: "Formulation", body: "Years of chemistry to keep Beta-T stable in every formula." },
];

export const ScienceHinokitiol = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
    <section
      ref={ref}
      className="relative bg-riversol-paper py-24 lg:py-32 overflow-hidden"
      data-testid="home-science"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* IMAGE — Beta-T botanical, mask + parallax reveal */}
        <motion.div
          initial={{ clipPath: "inset(8% 8% 8% 8%)", opacity: 0 }}
          animate={inView ? { clipPath: "inset(0% 0% 0% 0%)", opacity: 1 } : {}}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 lg:order-2 relative"
        >
          <div className="relative aspect-[5/4] lg:aspect-[6/5] overflow-hidden bg-neutral-200 rounded-sm">
            <motion.img
              src="https://www.riversol.com/cdn/shop/articles/beta-t-header_e4efe556-999b-4bea-a9a7-f3551efdb510.jpg?v=1774913889&width=1800"
              alt="Hinokitiol (Beta-Thujaplicin) — Pacific red cedar"
              className="absolute inset-0 w-full h-[114%] object-cover"
              style={{ y }}
              initial={{ scale: 1.06 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 8, ease: "linear" }}
              draggable={false}
            />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="hidden absolute left-5 top-5 font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-900 bg-white/90 backdrop-blur-sm px-3 py-1.5 whitespace-nowrap"
            >
              Beta-Thujaplicin · Pacific Red Cedar
            </motion.div>
          </div>
        </motion.div>

        {/* COPY */}
        <div className="lg:col-span-6 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="hidden font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500"
          >
            The science of Riversol
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 text-[38px] sm:text-[42px] lg:text-[56px] leading-[1.03] tracking-[-0.02em] text-neutral-900"
          >
            One tree.{" "}
            <span className="italic text-riversol-blue">Decades of research.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.75, delay: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 text-[16px] leading-[1.65] text-neutral-600 max-w-[540px]"
          >
            Hinokitiol — which Riversol formulates as Beta-T — is a natural
            compound found in the oil of the Pacific red cedar, with antioxidant,
            anti-inflammatory and anti-pigmentation properties. Dr. Rivers first
            encountered it through his research at the University of British
            Columbia, and spent years on the formulation chemistry needed to keep
            it stable — which is why you'll rarely find it anywhere else.
          </motion.p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-6">
            {FACTS.map((f, i) => (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 18 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.4 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="pt-4 border-t border-neutral-300/70"
                data-testid={`science-fact-${i}`}
              >
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-900">
                  {f.label}
                </div>
                <p className="mt-2 text-[13px] leading-relaxed text-neutral-500">{f.body}</p>
              </motion.div>
            ))}
          </div>

          <motion.a
            href="https://www.riversol.com/blogs/riversol/the-natural-benefits-of-beta-thujaplicin"
            data-testid="science-cta"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-11 inline-flex items-center gap-2 text-[13px] tracking-[0.14em] uppercase font-medium text-riversol-blue border-b border-riversol-blue/40 pb-2 hover:border-riversol-blue transition-colors"
          >
            Discover the science
            <ArrowRight size={16} strokeWidth={1.8} />
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default ScienceHinokitiol;
